import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import MysticalParticles from './MysticalParticles';
import { User, Lock, Mail, Eye, EyeOff } from 'lucide-react';

const MystveilAuth = () => {
  const location = useLocation();
  const isLogin = location.pathname.includes('/login');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (isLogin) {
        // Login
        const response = await fetch('https://api.4fingerstudios.com/api/auth/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            username: formData.username,
            password: formData.password
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `Login failed: ${response.status}`);
        }

        const data = await response.json();
        localStorage.setItem('mystveil_token', data.token);
        setSuccess('Login successful! Redirecting...');
        
        // Redirect to profile after successful login
        setTimeout(() => {
          window.location.href = '/profile';
        }, 1500);
      } else {
        // Register
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Passwords do not match');
        }

        const response = await fetch('https://api.4fingerstudios.com/api/auth/register', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            username: formData.username,
            password: formData.password,
            email: formData.email
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `Registration failed: ${response.status}`);
        }

        setSuccess('Account created successfully! Please check your email for verification. Make sure to check your spam folder.');
        setFormData({ username: '', password: '', email: '', confirmPassword: '' });
        
        // Store email for verification page
        localStorage.setItem('verification_email', formData.email);
        
        // Redirect to verification page after a short delay
        setTimeout(() => {
          window.location.href = `/auth/verify?email=${encodeURIComponent(formData.email)}`;
        }, 2000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Magical Background */}
      <div className="fixed inset-0 -z-10">
        <img 
          src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
          alt="Mystveil Township Map" 
          className="w-full h-full object-cover opacity-30 blur-sm" 
        />
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
      </div>

      {/* Magical Particles */}
      <MysticalParticles />

      {/* Navigation */}
      <Navbar />

      {/* Main Content */}
      <main className="flex-grow flex items-center justify-center px-4 py-8">
        <div className="max-w-md w-full">
          <div className="mystical-card">
            <div className="text-center mb-8">
              <h1 className="mystical-title text-black mb-4">
                {isLogin ? 'Mystveil Login' : 'Join Mystveil Beta'}
              </h1>
              <p className="mystical-text text-gray-600">
                {isLogin 
                  ? 'Welcome back to Mystveil Township!' 
                  : 'Create your account to join the Mystveil beta testing program'
                }
              </p>
            </div>

            {/* Success Message */}
            {success && (
              <div className="mystical-card bg-green-100 border border-green-400 text-green-700 mb-6">
                <p className="mystical-text">{success}</p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mystical-card bg-red-100 border border-red-400 text-red-700 mb-6">
                <p className="mystical-text">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Username */}
              <div>
                <label className="block mystical-text text-black font-bold mb-2">
                  Username *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                    placeholder="Enter your username"
                    required
                  />
                </div>
              </div>

              {/* Email (Register only) */}
              {!isLogin && (
                <div>
                  <label className="block mystical-text text-black font-bold mb-2">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                      placeholder="Enter your email address"
                      required
                    />
                  </div>
                </div>
              )}

              {/* Password */}
              <div>
                <label className="block mystical-text text-black font-bold mb-2">
                  Password *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                    placeholder="Enter your password"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password (Register only) */}
              {!isLogin && (
                <div>
                  <label className="block mystical-text text-black font-bold mb-2">
                    Confirm Password *
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                      placeholder="Confirm your password"
                      required
                      minLength={6}
                    />
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full mystical-button"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {isLogin ? 'Logging in...' : 'Creating account...'}
                  </>
                ) : (
                  isLogin ? 'Login to Mystveil' : 'Join Mystveil Beta'
                )}
              </button>
            </form>

            {/* Switch between Login/Register */}
            <div className="mt-6 text-center">
              <p className="mystical-text text-gray-600">
                {isLogin ? "Don't have an account? " : "Already have an account? "}
                <Link 
                  to={isLogin ? '/auth/register' : '/auth/login'}
                  className="mystical-text text-purple-800 hover:text-purple-900 font-bold"
                >
                  {isLogin ? 'Register here' : 'Login here'}
                </Link>
              </p>
            </div>

            {/* Beta Information */}
            {!isLogin && (
              <div className="mt-6 mystical-card bg-white bg-opacity-95">
                <h3 className="mystical-subtitle text-black mb-3">Beta Testing Program</h3>
                <ul className="mystical-text text-sm text-gray-600 space-y-2">
                  <li>• Get early access to Mystveil features</li>
                  <li>• Create up to 2 Veilkin characters</li>
                  <li>• Earn chips through artwork submissions</li>
                  <li>• Participate in community events</li>
                  <li>• Help shape the final game</li>
                </ul>
              </div>
            )}

            {/* Navigation Links */}
            <div className="mt-8 text-center">
              <Link to="/" className="mystical-button bg-gray-600 hover:bg-gray-700">
                Return Home
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Mystical Footer */}
      <footer className="mystical-nav py-6 mt-auto">
        <div className="container mx-auto px-4 text-center">
          <p className="mystical-text font-bold">
            © 2025 4fingerstudios <br /> 
            © 2024 Reckless Dreams. All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MystveilAuth;
